import { zodResolver } from "@hookform/resolvers/zod"
import { Button } from "design-system/atoms/Button"
import { Checkbox } from "design-system/atoms/Checkbox"
import { DialogFooter } from "design-system/atoms/Dialog"
import { Input } from "design-system/atoms/Input"
import { Popover, PopoverContent, PopoverTrigger } from "design-system/atoms/Popover"
import { Switch } from "design-system/atoms/Switch"
import { Command, CommandGroup, CommandItem, CommandList } from "design-system/molecules/Command"
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "design-system/organisms/Form"
import { getErrorTranslations } from "lib/i18n"
import { ChevronDown, Info } from "lucide-react"
import { useAuthEmployeeQuery } from "modules/auth/hooks/useAuthEmployeeQuery"
import { useState } from "react"
import { SubmitHandler, useForm } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { InviteEmployeeSchema } from "schema"
import { z } from "zod"

type FieldValues = z.infer<typeof InviteEmployeeSchema>

type Props = {
  onSubmit: SubmitHandler<FieldValues>
  isPending: boolean
}

export function InviteEmployeeForm({ onSubmit, isPending }: Props) {
  const { t } = useTranslation()
  const { data: currentUser } = useAuthEmployeeQuery()

  const companies = currentUser?.organization.companies ?? []
  const isMultiCompany = companies.length > 1
  const [isManager, setIsManager] = useState(false)

  const form = useForm<FieldValues>({
    resolver: zodResolver(InviteEmployeeSchema),
    defaultValues: {
      email: "",
      companyIds: companies.length === 1 ? [companies.at(0)?.id] : [],
    },
  })

  const watchedCompanyIds = form.watch("companyIds")
  const isSubmitDisabled =
    isPending ||
    (!isManager && isMultiCompany && (!watchedCompanyIds || watchedCompanyIds.length === 0))

  return (
    <Form
      {...form}
      errorTranslations={getErrorTranslations()}
      onSubmit={form.handleSubmit(onSubmit)}
    >
      <div className="space-y-2 flex flex-col gap-4">
        <FormField
          control={form.control}
          name="name"
          render={({ field }) => (
            <FormItem className="flex flex-col gap-2">
              <FormLabel>{t("settings.team.inviteEmployee.name")}</FormLabel>
              <FormControl>
                <Input type="text" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="email"
          render={({ field }) => (
            <FormItem className="flex flex-col gap-2">
              <FormLabel>{t("settings.team.inviteEmployee.email")}</FormLabel>
              <FormControl>
                <Input
                  type="email"
                  placeholder={t("settings.team.inviteEmployee.emailPlaceholder", {
                    domain: currentUser?.email.split("@")[1] || "example.com",
                  })}
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="companyIds"
          render={({ field }) => {
            const selectedCompanyIds = field.value ?? []
            const selectedCompanies = companies.filter((company) =>
              selectedCompanyIds.includes(company.id),
            )

            let selectedLabel: string
            if (isManager) {
              selectedLabel = t("settings.team.inviteEmployee.manager")
            } else if (selectedCompanies.length === 1) {
              selectedLabel = selectedCompanies[0]?.name ?? ""
            } else if (selectedCompanies.length > 1) {
              selectedLabel = selectedCompanies.map((c) => c.name).join(", ")
            } else {
              selectedLabel = ""
            }

            function handleManagerToggle(enabled: boolean) {
              setIsManager(enabled)

              if (enabled) {
                field.onChange([])
              } else if (!isMultiCompany && companies.length === 1) {
                field.onChange([companies[0]?.id])
              }
            }

            function toggleCompany(companyId: string) {
              if (isManager) return

              const value = field.value ?? []
              if (value.includes(companyId)) {
                const newValue = value.filter((id: string) => id !== companyId)
                field.onChange(newValue)
              } else {
                field.onChange([...value, companyId])
              }
            }

            return (
              <FormItem className="flex flex-col gap-2">
                <div className="flex items-center justify-between gap-2">
                  <FormLabel>
                    {isMultiCompany
                      ? t("settings.team.inviteEmployee.companyAccess")
                      : t("settings.team.inviteEmployee.role")}
                  </FormLabel>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-muted-foreground">
                      {t("settings.team.inviteEmployee.manager")}
                    </span>
                    <Switch checked={isManager} onCheckedChange={handleManagerToggle} />
                  </div>
                </div>
                {isMultiCompany && (
                  <>
                    <FormDescription className="text-xs">
                      {t("settings.team.inviteEmployee.companyAccessDescription")}
                    </FormDescription>
                    <FormControl>
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button
                            variant="outline"
                            className="w-full justify-between font-normal"
                            disabled={isManager}
                          >
                            <span className="truncate text-left">
                              {selectedLabel ||
                                t("settings.team.inviteEmployee.companyAccessPlaceholder")}
                            </span>
                            <ChevronDown className="h-5 w-5 shrink-0" />
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="p-0 min-w-56" align="start">
                          <Command>
                            <CommandList>
                              <CommandGroup>
                                {companies.map((company) => {
                                  const checked = selectedCompanyIds.includes(company.id)
                                  return (
                                    <CommandItem
                                      key={company.id}
                                      value={company.id}
                                      onSelect={() => toggleCompany(company.id)}
                                      className="gap-2"
                                    >
                                      <Checkbox checked={checked} />
                                      <span className="truncate">{company.name}</span>
                                    </CommandItem>
                                  )
                                })}
                              </CommandGroup>
                            </CommandList>
                          </Command>
                        </PopoverContent>
                      </Popover>
                    </FormControl>
                  </>
                )}
                <div className="flex items-start gap-2 text-xs text-muted-foreground">
                  <Info className="h-4 w-4 shrink-0 mt-0.5" />
                  <div>{t("settings.team.inviteEmployee.managerInfo")}</div>
                </div>
                <FormMessage />
              </FormItem>
            )
          }}
        />
      </div>
      <DialogFooter>
        <Button disabled={isSubmitDisabled}>{t("settings.team.inviteEmployee.submit")}</Button>
      </DialogFooter>
    </Form>
  )
}
